import { useState, FormEvent } from 'react';

const FORM_URL = 'https://readdy.ai/api/form/d758pej41deorl02f6vg';

const treatTypes = ['Custom Cake', 'Cookies', 'Pastries', 'Pies', 'Cheesecake', 'Cake Pops', 'Cupcakes', 'Other'];

export default function OrderFormSection() {
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [charCount, setCharCount] = useState(0);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (charCount > 500) return;

    setStatus('submitting');
    const form = e.currentTarget;
    const data = new URLSearchParams();

    const elements = form.elements as HTMLFormControlsCollection;
    Array.from(elements).forEach((el) => {
      const input = el as HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
      if (input.name && input.value) {
        data.append(input.name, input.value);
      }
    });

    try {
      const res = await fetch(FORM_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: data.toString(),
      });
      if (res.ok) {
        setStatus('success');
        form.reset();
        setCharCount(0);
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <section id="contact" className="py-20 md:py-28 bg-warm-100">
      <div className="max-w-3xl mx-auto px-4 md:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="h-px w-10 bg-gold-500" />
            <span className="text-gold-500 text-xs uppercase tracking-[0.25em] font-sans font-semibold">Get Started</span>
            <div className="h-px w-10 bg-gold-500" />
          </div>
          <h2 className="font-serif text-4xl md:text-5xl text-charcoal-800 mb-4">
            Request Your<br /><em className="italic text-blush-500">Custom Order</em>
          </h2>
          <p className="text-warm-gray text-base font-sans font-light leading-relaxed">
            Tell us about your celebration and we&apos;ll create something truly special for you.
          </p>
        </div>

        {/* Success */}
        {status === 'success' && (
          <div className="bg-green-50 border border-green-200 rounded-2xl p-6 mb-8 text-center">
            <div className="w-12 h-12 flex items-center justify-center rounded-full bg-green-100 text-green-600 mx-auto mb-3">
              <i className="ri-check-line text-xl" />
            </div>
            <h3 className="font-serif text-xl text-green-800 mb-2">Order Request Sent!</h3>
            <p className="text-green-700 text-sm font-sans">Thank you! We&apos;ll be in touch soon to create something special for you.</p>
          </div>
        )}

        {/* Error */}
        {status === 'error' && (
          <div className="bg-red-50 border border-red-200 rounded-2xl p-4 mb-6 text-center">
            <p className="text-red-700 text-sm font-sans">Something went wrong. Please try again or reach out on social media.</p>
          </div>
        )}

        {/* Form */}
        <form
          data-readdy-form
          id="custom-order-form"
          onSubmit={handleSubmit}
          className="bg-white rounded-2xl p-8 md:p-10 flex flex-col gap-5"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Full Name *</label>
              <input
                type="text"
                name="name"
                required
                placeholder="Your name"
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Email Address *</label>
              <input
                type="email"
                name="email"
                required
                placeholder="your@email.com"
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Phone Number</label>
              <input
                type="tel"
                name="phone"
                placeholder="(806) 555-0000"
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray"
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Event Date *</label>
              <input
                type="date"
                name="event_date"
                required
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 text-charcoal-800"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Type of Treat *</label>
              <select
                name="treat_type"
                required
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 text-charcoal-800 cursor-pointer"
              >
                <option value="">Select a treat...</option>
                {treatTypes.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-charcoal-800 text-sm font-sans font-semibold">Number of Servings</label>
              <input
                type="number"
                name="servings"
                min="1"
                placeholder="e.g. 24"
                className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray"
              />
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-charcoal-800 text-sm font-sans font-semibold">Theme or Occasion</label>
            <input
              type="text"
              name="theme_occasion"
              placeholder="e.g. Princess Birthday, Rustic Wedding, Graduation..."
              className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-charcoal-800 text-sm font-sans font-semibold">
              Design Ideas &amp; Description
              <span className={`ml-2 text-xs font-normal ${charCount > 500 ? 'text-red-500' : 'text-warm-gray'}`}>
                ({charCount}/500)
              </span>
            </label>
            <textarea
              name="design_description"
              rows={4}
              maxLength={500}
              placeholder="Describe your dream design — colors, themes, flavors, any inspiration..."
              className="border border-warm-200 rounded-lg px-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray resize-none"
              onChange={(e) => setCharCount(e.target.value.length)}
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-charcoal-800 text-sm font-sans font-semibold">Upload Inspiration Photo</label>
            <div className="border-2 border-dashed border-blush-200 rounded-lg p-6 text-center bg-blush-50/50">
              <i className="ri-upload-cloud-2-line text-2xl text-blush-400 mb-2 block" />
              <p className="text-warm-gray text-sm font-sans">Photo uploads are not collected online.</p>
              <p className="text-warm-gray text-xs font-sans mt-1">Please describe your inspiration in the field above, or share via social media/email.</p>
            </div>
          </div>

          <div className="flex flex-col gap-1.5">
            <label className="text-charcoal-800 text-sm font-sans font-semibold">Preferred Budget</label>
            <div className="relative">
              <span className="absolute left-4 top-1/2 -translate-y-1/2 text-warm-gray font-sans text-sm">$</span>
              <input
                type="text"
                name="budget"
                placeholder="e.g. 50–150"
                className="border border-warm-200 rounded-lg pl-8 pr-4 py-3 text-sm font-sans focus:outline-none focus:border-blush-400 transition-colors bg-warm-100/50 placeholder-warm-gray w-full"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={status === 'submitting' || charCount > 500}
            className="mt-2 w-full bg-blush-500 hover:bg-blush-600 disabled:opacity-60 disabled:cursor-not-allowed text-white py-4 rounded-full font-sans font-semibold text-sm tracking-wide whitespace-nowrap flex items-center justify-center gap-2 transition-all duration-200 hover:scale-[1.01] cursor-pointer"
          >
            {status === 'submitting' ? (
              <>
                <i className="ri-loader-4-line animate-spin" />
                Sending Your Request...
              </>
            ) : (
              <>
                Submit Your Sweet Order Request
                <i className="ri-send-plane-line" />
              </>
            )}
          </button>
        </form>
      </div>
    </section>
  );
}
